import { toast } from "react-toastify";
import { Logout } from "../Components/Auth/isAuthenticated";
import { api } from "./Api";
import { establishmentQuery } from "./selectedEstablishment";
import { IErroRest, IRest, IRestGetIBanner, IRestPaginate } from "./Interfaces";
import { getApiErrorMessage } from "./apiError";

/**
 * Escopo da listagem de banners no painel admin.
 *
 *   institutional → enviados pelo próprio admin. Não passam por aprovação.
 *   showcase      → enviados por estabelecimentos nos planos Pulse e Wave.
 *   connection    → enviados por estabelecimentos no plano Connection.
 *
 * Showcase e connection passam por análise de arte; institutional não.
 */
export type BannerScope =
  | "institutional"
  | "showcase"
  | "connection"
  | "all";

export const getBannerAll = async (
  offset: number,
  limit: number,
  scope: BannerScope = "institutional"
) => {
  try {
    return toast.promise(
      new Promise((resolve, reject) => {
        api
          .get(`/pt_BR/banners?offset=${offset}&limit=${limit}&scope=${scope}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            responseType: "json",
          })
          .then((response: IRestPaginate) => {
            return resolve(response);
          })
          .catch((error) => {
            return reject(error);
          });
      }),
      {
        error: {
          render(ToastContentProps) {
            const data = ToastContentProps.data as unknown as IErroRest;
            return data.response.data.message;
          },
        },
      }
    );
  } catch (erro) {
    Logout();
    return false;
  }
};

export const postBannerNew = async (data: {
  name: string;
  title: string;
  displayOrder: string;
  line: string;
  /** ULIDs dos países. Lista vazia = banner global, aparece para todo mundo. */
  countries?: string[];
}) => {
  try {
    return toast.promise(
      new Promise((resolve, reject) => {
        api
          .post(`/admin/banner`, data, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            responseType: "json",
          })
          .then((response: IRest) => {
            return resolve(response);
          })
          .catch((error) => {
            return reject(error);
          });
      }),
      {
        success: "Banner cadastrado com sucesso!",
        error: {
          render(ToastContentProps) {
            const data = ToastContentProps.data as unknown as IErroRest;
            return data.response.data.message;
          },
        },
      }
    );
  } catch (erro) {
    Logout();
    return false;
  }
};

export const getBannerID = async (id: string) => {
  try {
    return toast.promise(
      new Promise((resolve, reject) => {
        api
          .get(`/admin/banner/${id}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            responseType: "json",
          })
          .then((response: IRestGetIBanner) => {
            return resolve(response);
          })
          .catch((error) => {
            return reject(error);
          });
      }),
      {
        error: {
          render(ToastContentProps) {
            const data = ToastContentProps.data as unknown as IErroRest;
            return data.response.data.message;
          },
        },
      }
    );
  } catch (erro) {
    Logout();
    return false;
  }
};

export const putBanner = async (
  id: string,
  data: {
    displayOrder?: string;
    isActive?: string;
    url?: string;
    internalUrl?: string;
    /**
     * ULIDs dos países. Lista vazia limpa a segmentação (banner volta a ser
     * global). Omitir a chave mantém o que já estava gravado.
     */
    countries?: string[];
  }
) => {
  try {
    return toast.promise(
      new Promise((resolve, reject) => {
        api
          .put(`/admin/banner/${id}`, data, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            responseType: "json",
          })
          .then((response: IRest) => {
            return resolve(response);
          })
          .catch((error) => {
            return reject(error);
          });
      }),
      {
        error: {
          render(ToastContentProps) {
            const data = ToastContentProps.data as unknown as IErroRest;
            return data.response.data.message;
          },
        },
      }
    );
  } catch (erro) {
    Logout();
    return false;
  }
};

export const deleteBanner = async (id: string) => {
  try {
    return toast.promise(
      new Promise((resolve, reject) => {
        api
          .delete(`/admin/banner/${id}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            responseType: "json",
          })
          .then((response: IRest) => {
            return resolve(response);
          })
          .catch((error) => {
            return reject(error);
          });
      }),
      {
        error: {
          render(ToastContentProps) {
            const data = ToastContentProps.data as unknown as IErroRest;
            return data.response.data.message;
          },
        },
      }
    );
  } catch (erro) {
    Logout();
    return false;
  }
};

export const uploadImageBanner = async (id: string, formData: FormData) => {
  try {
    return toast.promise(
      new Promise((resolve, reject) => {
        api
          .post(`/admin/upload/Banner/${id}`, formData, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response: IRest) => {
            return resolve(response);
          })
          .catch((error) => {
            return reject(error);
          });
      }),
      {
        error: {
          render(ToastContentProps) {
            const data = ToastContentProps.data as unknown as IErroRest;
            return data.response.data.message;
          },
        },
      }
    );
  } catch (erro) {
    Logout();
    return false;
  }
};

// ─── Establishment Banner Functions ───────────────────────────────────────────
// Funções para o painel do estabelecimento. Banners criados aqui ficam
// vinculados ao userEstabilishment do usuário logado (campo nullable na
// entidade — banners do admin continuam com userEstabilishment = null).

export const getEstablishmentBanners = async (
  search: string = "",
  offset: number = 0,
  limit: number = 10
) => {
  try {
    return toast.promise(
      new Promise((resolve, reject) => {
        api
          .get(
            `/pt_BR/establishment/banners?search=${encodeURIComponent(search)}&offset=${offset}&limit=${limit}${establishmentQuery("&")}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
              responseType: "json",
            }
          )
          .then((response: IRestPaginate) => {
            return resolve(response);
          })
          .catch((error) => {
            return reject(error);
          });
      }),
      {
        error: {
          render(ToastContentProps) {
            const data = ToastContentProps.data as unknown as IErroRest;
            return data.response.data.message;
          },
        },
      }
    );
  } catch (erro) {
    Logout();
    return false;
  }
};

export const postEstablishmentBanner = async (data: {
  name: string;
  title?: string;
  displayOrder: string;
  line?: string;
  url: string;
  internalUrl: string;
  isActive?: string;
}) => {
  try {
    return toast.promise(
      new Promise((resolve, reject) => {
        api
          .post(`/pt_BR/establishment/banner${establishmentQuery()}`, data, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            responseType: "json",
          })
          .then((response: IRest) => {
            return resolve(response);
          })
          .catch((error) => {
            return reject(error);
          });
      }),
      {
        success: "Banner cadastrado com sucesso!",
        error: {
          render(ToastContentProps) {
            const data = ToastContentProps.data as unknown as IErroRest;
            return data.response.data.message;
          },
        },
      }
    );
  } catch (erro) {
    Logout();
    return false;
  }
};

export const putEstablishmentBanner = async (
  id: string,
  data: {
    name?: string;
    title?: string;
    displayOrder?: string;
    url?: string;
    internalUrl?: string;
    isActive?: string;
  }
) => {
  try {
    return toast.promise(
      new Promise((resolve, reject) => {
        api
          .put(`/pt_BR/establishment/banner/${id}${establishmentQuery()}`, data, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            responseType: "json",
          })
          .then((response: IRest) => {
            return resolve(response);
          })
          .catch((error) => {
            return reject(error);
          });
      }),
      {
        success: "Banner atualizado com sucesso!",
        error: {
          render(ToastContentProps) {
            const data = ToastContentProps.data as unknown as IErroRest;
            return data.response.data.message;
          },
        },
      }
    );
  } catch (erro) {
    Logout();
    return false;
  }
};

export const deleteEstablishmentBanner = async (id: string) => {
  try {
    return toast.promise(
      new Promise((resolve, reject) => {
        api
          .delete(`/pt_BR/banner/${id}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            responseType: "json",
          })
          .then((response: IRest) => {
            return resolve(response);
          })
          .catch((error) => {
            return reject(error);
          });
      }),
      {
        success: "Banner excluído com sucesso.",
        error: {
          render(ToastContentProps) {
            const data = ToastContentProps.data as unknown as IErroRest;
            return "Não foi possível excluir o banner. Por favor, tente novamente.";
          },
        },
      }
    );
  } catch (erro) {
    Logout();
    return false;
  }
};

export const repostEstablishmentBanner = async (id: string) => {
  try {
    return toast.promise(
      new Promise((resolve, reject) => {
        api
          .post(
            `/pt_BR/establishment/banner/${id}/repost${establishmentQuery()}`,
            {},
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
              responseType: "json",
            }
          )
          .then((response: IRest) => {
            return resolve(response);
          })
          .catch((error) => {
            return reject(error);
          });
      }),
      {
        success: "Banner repostado com sucesso!",
        error: {
          render(ToastContentProps) {
            const data = ToastContentProps.data as unknown as IErroRest;
            return data.response.data.message;
          },
        },
      }
    );
  } catch (erro) {
    Logout();
    return false;
  }
};

// Upload de imagem: reutiliza o endpoint admin existente (mesmo fluxo)
// Chame após postEstablishmentBanner para obter o ID do banner criado
export const uploadEstablishmentBannerImage = async (
  id: string,
  formData: FormData
) => {
  try {
    return toast.promise(
      new Promise((resolve, reject) => {
        api
          .post(`/pt_BR/upload/Banner/${id}`, formData, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response: IRest) => {
            return resolve(response);
          })
          .catch((error) => {
            return reject(error);
          });
      }),
      {
        error: {
          render(ToastContentProps) {
            const data = ToastContentProps.data as unknown as IErroRest;
            return data.response.data.message;
          },
        },
      }
    );
  } catch (erro) {
    Logout();
    return false;
  }
};
export const getBannerIDEstabilishment = async (id: string) => {
  try {
    return toast.promise(
      new Promise((resolve, reject) => {
        api
          .get(`/pt_BR/estabilishment/banner/${id}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            responseType: "json",
          })
          .then((response: IRestGetIBanner) => {
            return resolve(response);
          })
          .catch((error) => {
            return reject(error);
          });
      }),
      {
        error: {
          render(ToastContentProps) {
            const data = ToastContentProps.data as unknown as IErroRest;
            return data.response.data.message;
          },
        },
      }
    );
  } catch (erro) {
    Logout();
    return false;
  }
};
// Funções multipart — enviam FormData (com imagem) em uma única requisição
// Usadas pelo BannerForm para criar/editar banner com imagem no mesmo request

export const postEstablishmentBannerForm = async (formData: FormData) => {
  try {
    return toast.promise(
      new Promise((resolve, reject) => {
        api
          .post(`/pt_BR/establishment/banner${establishmentQuery()}`, formData, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response: IRest) => resolve(response))
          .catch((error) => reject(error));
      }),
      {
        success: "Banner cadastrado com sucesso!",
        error: {
          render(ToastContentProps) {
            const data = ToastContentProps.data as unknown as IErroRest;
            return data.response.data.message;
          },
        },
      }
    );
  } catch (erro) {
    Logout();
    return false;
  }
};

export const putEstablishmentBannerForm = async (id: string, formData: FormData) => {
  try {
    return toast.promise(
      new Promise((resolve, reject) => {
        api
          .post(`/pt_BR/establishment/banner/${id}${establishmentQuery()}`, formData, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response: IRest) => resolve(response))
          .catch((error) => reject(error));
      }),
      {
        success: "Banner atualizado com sucesso!",
        error: {
          render(ToastContentProps) {
            const data = ToastContentProps.data as unknown as IErroRest;
            return data.response.data.message;
          },
        },
      }
    );
  } catch (erro) {
    Logout();
    return false;
  }
};
// ─── Análise da arte (admin) ─────────────────────────────────────────────────

/**
 * Aprova ou recusa a arte de um banner.
 *
 * Aprovar coloca a arte na vitrine e INICIA a contagem do prazo contratado
 * (Pulse 15 dias / Wave 30 dias) — por isso a data de aprovação é o marco
 * zero, e não a data de criação do banner.
 *
 * Ao recusar, o motivo é obrigatório e chega ao anunciante por e-mail.
 */
export const reviewBannerArt = async (
  id: string,
  status: "approved" | "rejected",
  reason?: string,
) => {
  try {
    return toast.promise(
      api.put(
        `/admin/banner/${id}/art`,
        { status, reason: reason ?? null },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      ),
      {
        pending: status === "approved" ? "Aprovando arte..." : "Registrando recusa...",
        success:
          status === "approved"
            ? "Arte aprovada! Já está na vitrine."
            : "Recusa registrada. O anunciante foi avisado por e-mail.",
        error: {
          render({ data }) {
            return getApiErrorMessage(data, "Não foi possível registrar a análise.");
          },
        },
      },
    );
  } catch {
    return false;
  }
};